import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useSurveyTypeEdit() {
  const toast = useToast()

  // State
  const surveyTypeData = ref({
    id: null,
    title: '',
    typekey: '',
    description: '',
  })
  const tags = ref([])
  const isLoading = ref(false)
  const isSavingTag = ref(false)
  const isTagModalActive = ref(false)
  const editingTag = ref(null)
  const tagFormData = ref({
    title: '',
    typekey: '',
    description: '',
    tagorder: 1,
  })

  // Computed
  const sortedTags = computed(() => {
    return [...tags.value].sort((a, b) => (a.tagorder || 0) - (b.tagorder || 0))
  })

  // Table columns
  const tagTableColumns = [
    { key: 'tagorder', label: 'Order', sortable: true },
    { key: 'title', label: 'Title', sortable: true },
    { key: 'typekey', label: 'Type Key', sortable: true },
    { key: 'description', label: 'Description' },
    { key: 'actions', label: 'Actions' },
  ]

  // Methods
  const fetchTags = () => {
    if (!surveyTypeData.value.id) return

    store
      .dispatch('app-survey-type-edit/fetchSurveyTypeTags', {
        surveyTypeId: surveyTypeData.value.id,
      })
      .then(response => {
        tags.value = response.data.data || []
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching tags',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const initializeSurveyType = () => {
    const surveyTypeId = router.currentRoute.params.id
    const query = router.currentRoute.query
    
    if (surveyTypeId) {
      surveyTypeData.value = {
        id: surveyTypeId,
        title: query.title || '',
        typekey: query.typekey || '',
        description: query.description || '',
      }
      fetchTags()
    }
  }

  const updateSurveyType = () => {
    isLoading.value = true
    return store
      .dispatch('app-survey-type-edit/updateSurveyType', {
        id: surveyTypeData.value.id,
        surveyTypeData: surveyTypeData.value,
      })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        isLoading.value = false
        return true
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        isLoading.value = false
        return false
      })
  }

  const openTagModal = (tag = null) => {
    if (tag) {
      editingTag.value = tag
      tagFormData.value = {
        title: tag.title,
        typekey: tag.typekey,
        description: tag.description || '',
        tagorder: tag.tagorder || 1,
      }
    } else {
      editingTag.value = null
      tagFormData.value = {
        title: '',
        typekey: '',
        description: '',
        tagorder: tags.value.length + 1,
      }
    }
    isTagModalActive.value = true
  }

  const saveTag = () => {
    isSavingTag.value = true
    const payload = {
      ...tagFormData.value,
      SurveyTypeId: surveyTypeData.value.id,
    }

    const action = editingTag.value
      ? store.dispatch('app-survey-type-edit/updateSurveyTypeTag', {
          id: editingTag.value.id,
          tagData: payload,
        })
      : store.dispatch('app-survey-type-edit/addSurveyTypeTag', payload)

    return action
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        isTagModalActive.value = false
        fetchTags()
        isSavingTag.value = false
        return true
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        isSavingTag.value = false
        return false
      })
  }

  const deleteTag = (tagId) => {
    return store
      .dispatch('app-survey-type-edit/deleteSurveyTypeTag', {
        id: tagId,
      })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        fetchTags()
        return true
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return false
      })
  }

  return {
    // State
    surveyTypeData,
    tags,
    sortedTags,
    isLoading,
    isSavingTag,
    isTagModalActive,
    editingTag,
    tagFormData,
    tagTableColumns,

    // Methods
    initializeSurveyType,
    fetchTags,
    updateSurveyType,
    openTagModal,
    saveTag,
    deleteTag,
  }
}

